import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faGoogle, faMeta } from '@fortawesome/free-brands-svg-icons';
import {
  faArrowProgress,
  faAt,
  faAtlas,
  faBrowser,
  faBullseyePointer,
  faCommentDots,
  faEnvelope,
  faEnvelopeOpenText,
  faMapMarkerAlt,
  faMindShare,
  faRedo,
  faReplyAll,
  faSearch,
  faUsers,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import type { TFunction } from 'i18next';

import { CampaignClass, CampaignState } from '@feathr/blackbox';
import { EFontAwesomeKitIcon } from '@feathr/components';

export const campaignColorMap = new Map<CampaignClass, string>([
  [CampaignClass.Segment, 'cyan'],
  [CampaignClass.Lookalike, 'yellow'],
  [CampaignClass.SeedSegment, 'yellow'],
  [CampaignClass.Affinity, 'orange'],
  [CampaignClass.Search, 'orange'],
  [CampaignClass.EmailList, 'red'],
  [CampaignClass.MobileGeoFencing, 'indigo'],
  [CampaignClass.MobileGeoFenceRetargeting, 'indigo'],
  [CampaignClass.Referral, 'teal'],
  [CampaignClass.Conversation, 'blue'],
  [CampaignClass.LandingPage, 'green'],
  [CampaignClass.Facebook, 'cyan'],
  [CampaignClass.EmailListFacebook, 'red'],
  [CampaignClass.TrackedLink, 'red'],
  [CampaignClass.Drip, 'yellow'],
  // TODO: Set correct color for DripStep
  [CampaignClass.DripStep, 'yellow'],
  [CampaignClass.MailrailAutoSend, 'yellow'],
  [CampaignClass.MailrailDrip, 'yellow'],
  [CampaignClass.MailrailPartnerMessage, 'gray'],
  [CampaignClass.MailrailSingleSend, 'yellow'],
  [CampaignClass.PinpointEmail, 'yellow'],
  [CampaignClass.SmartPinpointEmail, 'yellow'],
  [CampaignClass.AutoPinpointEmail, 'yellow'],
  [CampaignClass.GoogleAdsSearch, 'cyan'],
  [CampaignClass.GoogleAdsSmart, 'cyan'],
  [CampaignClass.Omni, 'violet'],
]);

export const campaignStateColorMap = new Map<CampaignState, string>([
  [CampaignState.Draft, 'neutral'],
  [CampaignState.Complete, 'green'],
  [CampaignState.Completing, 'green'],
  [CampaignState.Erroring, 'orange'],
  [CampaignState.Published, 'green'],
  [CampaignState.Publishing, 'yellow'],
  [CampaignState.Scheduled, 'yellow'],
  [CampaignState.Stopped, 'red'],
]);

export const campaignIconMap = new Map<CampaignClass, IconDefinition>([
  [CampaignClass.Segment, faRedo],
  [CampaignClass.Lookalike, faUsers],
  [CampaignClass.SeedSegment, faUsers],
  [CampaignClass.Affinity, faWandMagicSparkles],
  [CampaignClass.Search, faSearch],
  [CampaignClass.EmailList, faAt],
  [CampaignClass.MobileGeoFencing, faMapMarkerAlt],
  [CampaignClass.MobileGeoFenceRetargeting, faAtlas],
  [CampaignClass.Referral, faEnvelope],
  [CampaignClass.Conversation, faCommentDots],
  [CampaignClass.LandingPage, faBrowser],
  [CampaignClass.GoogleAdsSearch, faGoogle],
  [CampaignClass.GoogleAdsSmart, faGoogle],
  [CampaignClass.Facebook, faMeta],
  [CampaignClass.EmailListFacebook, faMeta],
  [CampaignClass.TrackedLink, faBullseyePointer],
  [CampaignClass.Drip, faArrowProgress],
  // TODO: Set corrrect icon for DripStep
  [CampaignClass.DripStep, faArrowProgress],
  [CampaignClass.MailrailAutoSend, faReplyAll],
  [CampaignClass.MailrailDrip, faArrowProgress],
  [CampaignClass.MailrailPartnerMessage, faEnvelopeOpenText],
  [CampaignClass.MailrailSingleSend, faEnvelopeOpenText],
  [CampaignClass.PinpointEmail, faEnvelopeOpenText],
  [CampaignClass.SmartPinpointEmail, faMindShare],
  [CampaignClass.AutoPinpointEmail, faReplyAll],
]);

export const customCampaignIconMap = new Map<CampaignClass, EFontAwesomeKitIcon>([
  [CampaignClass.Omni, EFontAwesomeKitIcon.pixlPlus],
]);

export const campaignStateLabelMap = (t: TFunction, state: CampaignState): string => {
  const map = {
    [CampaignState.Draft]: t('Draft'),
    [CampaignState.Complete]: t('Complete'),
    [CampaignState.Completing]: t('Completing'),
    [CampaignState.Erroring]: t('Erroring'),
    [CampaignState.Published]: t('Published'),
    [CampaignState.Publishing]: t('Publishing'),
    [CampaignState.Scheduled]: t('Scheduled'),
    [CampaignState.Stopped]: t('Stopped'),
  };

  return map[state];
};
